import React, { useEffect, useState, useCallback, useRef } from "react";
import { useIntl, FormattedDate } from "react-intl";
import { useHistory } from "react-router-dom";
import { FileText, Plus, Upload, Search } from "react-feather";
import { debouncer } from "../../utils/debouncer";
import Skeleton from "../../components/Skeleton";
import Pagination from "../../components/Pagination";
import FeedbackMessage from "../../components/FeedbackMessage";
import { api, getOperationsHistory } from "../../api";
import { useAmbient } from "../../hooks/ambient";
import Multiselect from "../../components/Multiselect";
import "./styles.scss";
import ImportOperationsModal from "./components/ImportOperationsModal";

const PAGE_SIZE = 20;

const IMPORT_OPERATIONS_MODAL = "IMPORT_OPERATIONS_MODAL";

const Operations = ({ setError }) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [filterOptions, setFilterOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [openModal, setOpenModal] = useState(null);
  const analysisErrorRef = useRef(null);

  const { isEnterpriseEnv, ambient } = useAmbient();

  const handleRowClick = operation => {
    history.push(`/operation-results/${operation.id}`);
  };

  const handleCreateOperationClick = () => {
    history.push("/new-operation");
  };

  const fetchOperations = useCallback(
    (options = {}) => {
      setLoading(true);

      getOperationsHistory(page, PAGE_SIZE, options)
        .then(res => {
          setOperations(res.data.data);
          setTotalPages(res.data.totalPages);
        })
        .catch(() => setError(formatMessage({ id: "erro-api" })))
        .finally(() => {
          setLoading(false);
        });
    },
    [setError, formatMessage, page]
  );

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  useEffect(() => {
    if (isEnterpriseEnv && ambient.admin)
      api
        .get("/b3_consultas/get_users_list_enterprise")
        .then(({ data: response }) => {
          if (response.admin) {
            const users = response.result.map(r => ({
              value: r.pessoa_id,
              label: r.nome
            }));
            setFilterOptions(users);
          } else {
            setError(formatMessage({ id: "erro-api" }));
          }
        })
        .catch(() => setError(formatMessage({ id: "erro-api" })));
  }, [setError, formatMessage, isEnterpriseEnv, ambient]);

  const handleChangeOnFilter = useCallback(
    (filters, nameSearch = null) => {
      const operationOptions = {};

      if (isEnterpriseEnv && ambient.admin) {
        if (filters.length > 0) {
          operationOptions["conditions"] = {
            "B3Lote.pessoa_id": filters.map(filter => filter.value)
          };
        }
      }

      if (nameSearch) {
        operationOptions["conditions"] = {
          ...operationOptions["conditions"],
          "B3Lote.nome ilike": `%${nameSearch}%`
        };
      }

      fetchOperations({
        isEnterpriseEnv,
        conditions: operationOptions.conditions
      });
    },
    [isEnterpriseEnv, ambient, fetchOperations]
  );

  useEffect(() => {
    handleChangeOnFilter(selectedFilters);
  }, [selectedFilters, handleChangeOnFilter]);

  const handleSearchBox = debouncer(nameSearch => {
    handleChangeOnFilter(selectedFilters, nameSearch);
  }, 1000);

  return (
    <>
      <div className="operation-header">
        <h1>{formatMessage({ id: "operacao" })}</h1>

        <div>
          <button onClick={() => setOpenModal(IMPORT_OPERATIONS_MODAL)}>
            <Upload size={20} />
            {formatMessage({ id: "importar-csv-operacoes" })}
          </button>

          <button onClick={() => handleCreateOperationClick()}>
            <Plus size={20} />
            Criar operação
          </button>
        </div>
      </div>

      <div className="content-box operation">
        <div className="operation-top">
          <div className="search-wrapper">
            <p className="modal-description">
              Pesquise na caixa abaixo e selecione o produto desejado
            </p>

            <div className="search-input">
              <Search size={20} />
              <input
                type="text"
                placeholder="Pesquise o código ou nome do produto desejado"
                onChange={e => handleSearchBox(e.target.value)}
              />
            </div>
          </div>

          {isEnterpriseEnv && ambient.admin && operations.length !== 0 && (
            <div className="operation-filter">
              <span>{formatMessage({ id: "filtrar-por-usuario" })}</span>
              <Multiselect
                selectAll
                options={filterOptions}
                onChange={e => setSelectedFilters(e)}
              />
            </div>
          )}
        </div>

        {!loading && operations.length === 0 ? (
          <div className="no-results">
            <FileText size="48px" />
            <p>{formatMessage({ id: "nenhuma-operacao-encontrada" })}</p>
          </div>
        ) : (
          <>
            <table className="table is-striped is-hoverable">
              <thead>
                <tr>
                  <th>{formatMessage({ id: "data-operacao" })}</th>
                  <th>{formatMessage({ id: "nome-operacao" })}</th>
                  {isEnterpriseEnv && (
                    <th>{formatMessage({ id: "solicitante-consulta" })}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading &&
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(5)].map((_, j) => (
                        <td key={j}>
                          <Skeleton />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!loading &&
                  operations.length > 0 &&
                  operations.map(operation => (
                    <tr
                      key={operation.B3Lote.id}
                      data-testid={`result-${operation.B3Lote.id}`}
                      onClick={() => handleRowClick(operation.B3Lote)}
                    >
                      <td>
                        <FormattedDate
                          value={new Date(operation.B3Lote.created)}
                          year="numeric"
                          month="numeric"
                          day="numeric"
                          hour="numeric"
                          minute="numeric"
                        />
                      </td>
                      <td>
                        {operation.B3Lote.nome || (
                          <span className="untitled">
                            {formatMessage({ id: "sem-titulo" })}
                          </span>
                        )}
                      </td>

                      {isEnterpriseEnv && (
                        <td>{operation.Pessoa.PessoaFisica.nome || ""}</td>
                      )}

                      {/* <td>
                        {rep.B3Lote.is_finalizado ? (
                          <CheckSquare
                            title={formatMessage({
                              id: "processada"
                            })}
                          />
                        ) : (
                          <Square
                            title={formatMessage({
                              id: "nao-processada"
                            })}
                          />
                        )}
                      </td> */}
                    </tr>
                  ))}
              </tbody>
            </table>

            <Pagination page={page} totalPages={totalPages} setPage={setPage} />
          </>
        )}
      </div>

      {analysisError && (
        <FeedbackMessage
          ref={analysisErrorRef}
          level="error"
          message={analysisError}
          onDismiss={() => setAnalysisError(null)}
        />
      )}
      {successMessage && (
        <FeedbackMessage
          style={{
            top: analysisErrorRef.current
              ? `${analysisErrorRef.current.clientHeight + 24}px`
              : undefined
          }}
          level="success"
          message={successMessage}
          onDismiss={() => setSuccessMessage(null)}
        />
      )}

      {openModal === IMPORT_OPERATIONS_MODAL && (
        <ImportOperationsModal
          onClose={() => setOpenModal(null)}
          onError={message => setError(message)}
          onSuccess={() => {
            fetchOperations();
            setOpenModal(null);
          }}
        />
      )}
    </>
  );
};

export default Operations;
